<template>
  <dialog ref="dialogRef" class="closed-spot-dialog">
    <div class="closed-spot-card">
      <div class="closed-spot-title">{{ localize('spark.closed_spot.title') }}</div>

      <div class="closed-spot-hours">
        <span class="closed-spot-hours-title">{{ localize('locations.detail.about.openingHours.title') }}</span>
        <div class="closed-spot-hours-scroll">
          <div
            v-for="(row, index) in schedule"
            :key="`day-${row.day}`"
            :class="['closed-spot-day', `day-${index}`, { 'is-active': row.day === highlightDay }]"
          >
            <span class="closed-spot-day-name">{{ localize(dayKeys[row.day]) }}</span>
            <span v-if="!row.start || !row.end" class="closed-spot-day-closed">
              {{ localize('locations.form.openHours.closed') }}
            </span>
            <span v-else class="closed-spot-day-span">{{ row.start }} &mdash; {{ row.end }}</span>
          </div>
        </div>
      </div>

      <div class="closed-spot-actions">
        <button type="button" class="closed-spot-change" @click="emit('change')">
          {{ localize('spark.closed_spot.change') }}
        </button>
        <button type="button" class="closed-spot-create" @click="emit('createAnyways')">
          {{ localize('spark.closed_spot.create_anyways') }}
        </button>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import type { OpeningHourEntry } from '@/core/helpers/opening-hours.helper';
import { getOpeningHoursSchedule } from '@/core/helpers/opening-hours.helper';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps<{
  openingHours: OpeningHourEntry[];
  highlightDay: number;
}>();

const emit = defineEmits<{
  createAnyways: [];
  change: [];
}>();

const dialogRef = ref<HTMLDialogElement>();

const dayKeys: Record<number, string> = {
  0: 'locations.form.openHours.sunday',
  1: 'locations.form.openHours.monday',
  2: 'locations.form.openHours.tuesday',
  3: 'locations.form.openHours.wednesday',
  4: 'locations.form.openHours.thursday',
  5: 'locations.form.openHours.friday',
  6: 'locations.form.openHours.saturday',
};

const schedule = computed(() => {
  const today = new Date().getDay();
  return [...getOpeningHoursSchedule(props.openingHours)].sort((a, b) => {
    const aOffset = (a.day - today + 7) % 7;
    const bOffset = (b.day - today + 7) % 7;
    return aOffset - bOffset;
  });
});

const handleCancel = (event: Event) => {
  event.preventDefault();
  emit('change');
};

onMounted(() => {
  dialogRef.value?.showModal();
  dialogRef.value?.addEventListener('cancel', handleCancel);
});

onBeforeUnmount(() => {
  dialogRef.value?.removeEventListener('cancel', handleCancel);
  if (dialogRef.value?.open) {
    dialogRef.value.close();
  }
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.closed-spot-dialog {
  width: 100%;
  height: 100%;
  border: 0;
  padding: var(--sai-top) 1rem var(--sai-bottom);
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }
}

.closed-spot-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: min(100%, 420px);
  padding: var(--spacing-xxl) var(--spacing-xl);
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.closed-spot-title {
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  color: var(--color-white);
  margin-bottom: var(--spacing-xl);
}

.closed-spot-hours {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: var(--spacing-xl);
}

.closed-spot-hours-title {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-white);
  margin-bottom: 0.75rem;
}

.closed-spot-hours-scroll {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 11.5rem;
  overflow-y: auto;
  gap: 0.75rem;
  padding-right: 0.25rem;
}

.closed-spot-day {
  display: flex;
  align-items: center;
  color: var(--color-white-70);
  font-size: 14px;
  letter-spacing: 0.05em;

  &.day-6 {
    opacity: 0.5;
  }

  &.day-5 {
    opacity: 0.6;
  }

  &.day-4 {
    opacity: 0.7;
  }

  &.day-3 {
    opacity: 0.8;
  }

  &.day-2 {
    opacity: 0.9;
  }

  &.day-0:not(.is-active) {
    & > span {
      color: var(--color-white);
      font-weight: 600;
    }
  }

  &.is-active {
    opacity: 1;
    color: var(--color-red);
    font-weight: 600;

    .closed-spot-day-name,
    .closed-spot-day-closed,
    .closed-spot-day-span {
      color: var(--color-red);
    }
  }
}

.closed-spot-day-name {
  width: 4rem;
  flex-shrink: 0;
  color: var(--color-white-40);
}

.closed-spot-day-closed {
  color: var(--color-white-40);
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 11px;
}

.closed-spot-day-span {
  letter-spacing: 0.1em;
}

.closed-spot-actions {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.closed-spot-create,
.closed-spot-change {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 2rem;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.closed-spot-change {
  background: var(--color-linear-gradient);
  color: #fff;
  transition: opacity 200ms ease;

  @include hover {
    opacity: 0.9;
  }

  @include press;
}

.closed-spot-create {
  margin-top: var(--spacing-md);
  border: 1px solid var(--color-white-10);
  background: transparent;
  color: var(--color-white-70);
  transition: all 200ms ease;

  @include hover {
    background: var(--color-white-5);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press;
}
</style>
